import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Icon from "../../../components/AppIcon";
import Input from "../../../components/ui/Input";
import { cn } from "../../../utils/cn";
import FakeAppChrome from "../FakeAppChrome";
import {
  TOUR_AI_MESSAGES,
  TOUR_AI_STATUS_LINES,
  TOUR_DOCUMENT,
  TOUR_FORM_FIELDS,
  TOUR_PREVIEW_LINES,
} from "../tourData";

const INITIAL_MSG = TOUR_AI_MESSAGES[0];
const USER_MSG = TOUR_AI_MESSAGES[1];
const SUCCESS_MSG = TOUR_AI_MESSAGES[2];

function TourFillMagicProgress() {
  const [statusIndex, setStatusIndex] = useState(0);

  useEffect(() => {
    const id = window.setInterval(() => {
      setStatusIndex((prev) => (prev + 1) % TOUR_AI_STATUS_LINES.length);
    }, 900);
    return () => window.clearInterval(id);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 6 }}
      className="relative mr-2 overflow-hidden rounded-xl border border-primary/20 bg-background px-3 py-3 shadow-sm"
    >
      <motion.div
        className="pointer-events-none absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent"
        animate={{ x: ["-120%", "120%"] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        aria-hidden
      />
      <div className="relative z-[1] flex items-start gap-3">
        <div className="relative mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center">
          <motion.span
            className="absolute inset-0 rounded-full bg-primary/15"
            animate={{ scale: [1, 1.25, 1], opacity: [0.55, 0.2, 0.55] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden
          />
          <motion.span
            animate={{ rotate: [0, 12, -8, 0], scale: [1, 1.08, 1] }}
            transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
          >
            <Icon name="Sparkles" size={18} className="text-primary" />
          </motion.span>
        </div>
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="min-h-[1.25rem]">
            <AnimatePresence mode="wait">
              <motion.p
                key={statusIndex}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.22 }}
                className="text-sm font-medium tracking-tight text-foreground"
              >
                {TOUR_AI_STATUS_LINES[statusIndex]}
              </motion.p>
            </AnimatePresence>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-muted">
            <motion.div
              className="h-full rounded-full bg-primary"
              initial={{ width: "10%" }}
              animate={{ width: ["14%", "48%", "72%", "90%"] }}
              transition={{ duration: 2.4, ease: "easeInOut" }}
            />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/** NeoAi dock — mirrors DocumentFillerChatPanel chrome (floating over preview). */
function TourNeoAiPanel({ messages, loading, composerText }) {
  return (
    <div className="flex w-full max-w-[22.5rem] flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-elevation-lg">
      <div className="flex shrink-0 items-center justify-between gap-2 bg-primary px-3 py-2.5 text-primary-foreground">
        <span className="flex items-center gap-2 text-sm font-semibold tracking-tight">
          <Icon name="Sparkles" size={18} />
          NeoAi
        </span>
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-md bg-primary-foreground/15 px-2 py-1 text-xs font-medium tabular-nums">
            <Icon name="Coins" size={14} aria-hidden />
            120
          </span>
          <span className="rounded-md p-1.5 opacity-80">
            <Icon name="X" size={18} />
          </span>
        </div>
      </div>

      <div className="min-h-[180px] max-h-[min(44vh,19rem)] flex-1 space-y-3 overflow-y-auto bg-muted/20 px-3 py-2">
        <AnimatePresence initial={false}>
          {messages.map((message, index) => (
            <motion.div
              key={`${message.role}-${index}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className={cn(
                "rounded-lg px-3 py-2 text-sm leading-relaxed",
                message.role === "user"
                  ? "ml-4 bg-primary/10 text-foreground"
                  : "mr-2 border border-border bg-background"
              )}
            >
              <span className="whitespace-pre-wrap">{message.text}</span>
            </motion.div>
          ))}
        </AnimatePresence>
        <AnimatePresence mode="wait">
          {loading ? <TourFillMagicProgress key="loading" /> : null}
        </AnimatePresence>
      </div>

      <div className="shrink-0 border-t border-border bg-card p-2">
        <div className="min-h-[2.75rem] rounded-lg border border-input bg-background px-3 py-2 text-sm text-muted-foreground">
          {composerText || (
            <span className="opacity-60">
              Ex.: Preencha a autorização com os dados do imóvel…
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

const FillDocumentScene = () => {
  const [messages, setMessages] = useState([INITIAL_MSG]);
  const [loading, setLoading] = useState(false);
  const [filledCount, setFilledCount] = useState(0);
  const [composerText, setComposerText] = useState("");

  useEffect(() => {
    const timers = [];

    timers.push(
      window.setTimeout(() => {
        setComposerText("");
        let i = 0;
        const full = USER_MSG.text;
        const typeId = window.setInterval(() => {
          i += 3;
          setComposerText(full.slice(0, i));
          if (i >= full.length) window.clearInterval(typeId);
        }, 16);
        timers.push(typeId);
      }, 500)
    );

    timers.push(
      window.setTimeout(() => {
        setComposerText("");
        setMessages([INITIAL_MSG, USER_MSG]);
        setLoading(true);
      }, 2200)
    );

    timers.push(
      window.setTimeout(() => {
        setLoading(false);
        setMessages([INITIAL_MSG, USER_MSG, SUCCESS_MSG]);
        let n = 0;
        const fillId = window.setInterval(() => {
          n += 1;
          setFilledCount(n);
          if (n >= TOUR_FORM_FIELDS.length) window.clearInterval(fillId);
        }, 160);
        timers.push(fillId);
      }, 5000)
    );

    return () => {
      timers.forEach((id) => {
        window.clearTimeout(id);
        window.clearInterval(id);
      });
    };
  }, []);

  const previewText = useMemo(() => {
    if (filledCount < TOUR_FORM_FIELDS.length) {
      return "A prévia será atualizada conforme a NeoAi preenche o formulário…";
    }
    return TOUR_PREVIEW_LINES.join("\n");
  }, [filledCount]);

  return (
    <FakeAppChrome activeNavId="generator">
      <div className="flex min-h-full flex-col">
        <header className="shrink-0 border-b border-border/80 bg-card px-4 py-4 sm:px-6">
          <h1 className="text-xl font-bold truncate sm:text-2xl lg:text-3xl">
            Gerar Documento
          </h1>
          <p className="mt-1 text-sm text-muted-foreground truncate">
            Modelo: {TOUR_DOCUMENT.modelName}
          </p>
        </header>

        <div className="relative grid flex-1 grid-cols-1 lg:grid-cols-2">
          <div className="lg:border-r lg:border-border/60">
            <div className="space-y-3 p-4 sm:p-6">
              {/* Mobile: NeoAi above form (readable); desktop docks over preview */}
              <div className="lg:hidden">
                <TourNeoAiPanel
                  messages={messages}
                  loading={loading}
                  composerText={composerText}
                />
              </div>

              <div className="space-y-3 pb-2">
                {TOUR_FORM_FIELDS.map((field, index) => {
                  const filled = index < filledCount;
                  const justFilled = index === filledCount - 1;
                  const showSection =
                    index === 0 ||
                    field.section !== TOUR_FORM_FIELDS[index - 1]?.section;
                  return (
                    <React.Fragment key={field.id}>
                      {showSection ? (
                        <p
                          className={cn(
                            "pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground",
                            index === 0 && "pt-0"
                          )}
                        >
                          {field.section}
                        </p>
                      ) : null}
                      <motion.div
                        initial={false}
                        animate={
                          justFilled ? { scale: [1, 1.01, 1] } : { scale: 1 }
                        }
                        transition={{ duration: 0.35 }}
                        className={cn(
                          "rounded-lg transition-[box-shadow,background-color] duration-300",
                          justFilled && "ring-2 ring-primary/45 bg-primary/5",
                          filled && !justFilled && "bg-success/5"
                        )}
                      >
                        <Input
                          label={field.label}
                          value={filled ? field.value : ""}
                          placeholder={filled ? undefined : "Aguardando NeoAi…"}
                          readOnly
                          tabIndex={-1}
                        />
                      </motion.div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sticky so NeoAi stays in the first viewport while the tall form scrolls */}
          <div className="relative hidden min-h-[28rem] self-start bg-muted/20 p-4 sm:p-6 lg:sticky lg:top-0 lg:block lg:max-h-[min(100dvh,52rem)]">
            <p className="mb-3 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Prévia ao vivo
            </p>
            <motion.div
              key={filledCount >= TOUR_FORM_FIELDS.length ? "full" : "empty"}
              initial={{ opacity: 0.5 }}
              animate={{ opacity: 1 }}
              className="min-h-[280px] rounded-lg border border-border bg-card p-6 font-serif text-sm leading-relaxed text-foreground shadow-soft-sm whitespace-pre-line"
            >
              {previewText}
            </motion.div>

            {/* Docked near the top so the fill animation is visible on step entry */}
            <div className="absolute top-6 right-6 z-[2]">
              <TourNeoAiPanel
                messages={messages}
                loading={loading}
                composerText={composerText}
              />
            </div>
          </div>
        </div>
      </div>
    </FakeAppChrome>
  );
};

export default FillDocumentScene;
